import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { 
  CaretDown, 
  CaretUp,
  Fire,
  Thermometer,
  Snowflake,
  Target,
  CheckCircle,
  Globe,
  WhatsappLogo,
  ArrowSquareOut,
  Bell
} from "@phosphor-icons/react";
import MessageHistory from "./MessageHistory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadStatusBadge = ({ status }) => {
  const config = {
    hot: { 
      className: "badge badge-hot", 
      icon: Fire, 
      label: "Hot"
    },
    warm: { 
      className: "badge badge-warm", 
      icon: Thermometer, 
      label: "Warm"
    },
    cold: { 
      className: "badge badge-cold", 
      icon: Snowflake, 
      label: "Cold"
    },
    converted: { 
      className: "badge badge-converted", 
      icon: Target, 
      label: "Converted"
    }
  };

  const { className, icon: Icon, label } = config[status] || config.cold;

  return (
    <span className={className} data-testid={`lead-status-badge-${status}`}>
      <Icon size={14} weight="bold" />
      {label}
    </span>
  );
};

const ChannelBadge = ({ source, isBridged }) => {
  if (isBridged) {
    return (
      <span className="badge badge-bridged" data-testid="channel-badge-bridged">
        <Globe size={14} weight="bold" />
        <span className="mx-1">+</span>
        <WhatsappLogo size={14} weight="bold" />
        Bridged
      </span>
    );
  }

  if (source === "whatsapp") {
    return (
      <span className="badge badge-whatsapp" data-testid="channel-badge-whatsapp">
        <WhatsappLogo size={14} weight="bold" />
        WhatsApp
      </span>
    );
  }

  return (
    <span className="badge badge-website" data-testid="channel-badge-website">
      <Globe size={14} weight="bold" />
      Website
    </span>
  );
};

const DemoBookedBadge = ({ booked }) => {
  if (!booked) return <span className="text-[#707070]">—</span>;
  
  return (
    <span className="badge badge-demo" data-testid="demo-booked-badge">
      <CheckCircle size={14} weight="bold" />
      Demo Booked
    </span>
  );
};

const HandoffBadge = ({ sent }) => {
  if (!sent) return <span className="text-[#707070]">—</span>;
  
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/30" data-testid="handoff-sent-badge">
      <ArrowSquareOut size={14} weight="bold" />
      Handoff Sent
    </span>
  );
};

const HotAlertBadge = ({ sent }) => {
  if (!sent) return <span className="text-[#707070]">—</span>;
  
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold bg-[#FF9F0A]/15 text-[#FF9F0A] border border-[#FF9F0A]/30" data-testid="hot-alert-sent-badge">
      <Bell size={14} weight="bold" />
      Hot Alert Sent
    </span>
  );
};

const ScoreBadge = ({ score }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return "#FF3B30";
    if (score >= 50) return "#FFD60A";
    return "#64D2FF";
  };

  return (
    <span 
      className="font-mono text-lg font-bold"
      style={{ color: getScoreColor(score) }}
      data-testid="session-score"
    >
      {score}
    </span>
  );
};

const ProductInterestBadge = ({ interest }) => {
  if (!interest) return <span className="text-[#707070]">—</span>;
  
  const labels = {
    ultragym: "UltraGym",
    studio: "Studio",
    both: "Both"
  };

  return (
    <span className="text-white text-sm font-medium">
      {labels[interest] || interest}
    </span>
  );
};

const SessionRow = ({ session, isExpanded, onToggle, onSessionClick }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const handleToggle = async () => {
    if (!isExpanded && messages.length === 0) {
      setLoadingMessages(true);
      try {
        const response = await axios.get(`${API}/sessions/${session.id}/messages`);
        setMessages(response.data);
      } catch (err) {
        console.error("Error fetching messages:", err);
      } finally {
        setLoadingMessages(false);
      }
    }
    onToggle();
  };

  return (
    <>
      <tr 
        className="cursor-pointer"
        onClick={handleToggle}
        data-testid={`session-row-${session.id}`}
      >
        <td className="w-10">
          <button 
            className="p-1 hover:bg-[#2A2A2A] rounded transition-colors"
            data-testid={`row-expand-button-${session.id}`}
          >
            {isExpanded ? (
              <CaretUp size={18} className="text-[#A0A0A0]" />
            ) : (
              <CaretDown size={18} className="text-[#A0A0A0]" />
            )}
          </button>
        </td>
        <td className="text-sm text-[#A0A0A0]">
          {format(new Date(session.created_at), "MMM d, yyyy HH:mm")}
        </td>
        <td>
          <ScoreBadge score={session.score} />
        </td>
        <td>
          <LeadStatusBadge status={session.lead_status} />
        </td>
        <td>
          <ProductInterestBadge interest={session.product_interest} />
        </td>
        <td className="text-white font-medium">
          <button
            onClick={(e) => { e.stopPropagation(); onSessionClick(session.id); }}
            data-testid={`session-name-${session.id}`}
            className="text-white hover:text-[#E85D24] transition-colors text-left"
          >
            {session.visitor_name || <span className="text-[#707070]">Anonymous</span>}
          </button>
        </td>
        <td className="text-[#A0A0A0] text-sm font-mono">
          {session.visitor_phone || <span className="text-[#707070]">—</span>}
        </td>
        <td className="text-[#A0A0A0] text-sm">
          {session.visitor_email || <span className="text-[#707070]">—</span>}
        </td>
        <td>
          <ChannelBadge source={session.source} isBridged={session.is_bridged} />
        </td>
        <td>
          <DemoBookedBadge booked={session.demo_booked} />
        </td>
        <td>
          <HandoffBadge sent={session.handoff_sent} />
        </td>
        <td>
          <HotAlertBadge sent={session.hot_alert_sent} />
        </td>
        <td className="text-xs text-[#A0A0A0]">
          {session.handoff_initiated_at 
            ? format(new Date(session.handoff_initiated_at), "MMM d, HH:mm")
            : <span className="text-[#707070]">—</span>
          }
        </td>
      </tr>
      {isExpanded && (
        <tr className="expanded-row" data-testid={`session-messages-${session.id}`}>
          <td colSpan={13} className="p-0">
            <MessageHistory 
              messages={messages} 
              loading={loadingMessages}
              sessionId={session.id}
              handoffSent={session.handoff_sent}
              handoffBrief={session.handoff_brief}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const SessionsTable = ({ sessions, loading, onSessionClick }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (sessionId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div 
        className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-8 text-center"
        data-testid="sessions-loading"
      >
        <p className="text-[#A0A0A0]">Loading sessions...</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div 
        className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-8 text-center"
        data-testid="sessions-empty"
      >
        <p className="text-[#A0A0A0] mb-2">No sessions yet</p>
        <p className="text-sm text-[#707070]">
          Click "Seed Sample Data" to add demo sessions
        </p>
      </div>
    );
  }

  return (
    <div 
      className="bg-[#121212] border border-[#2A2A2A] rounded-sm overflow-hidden"
      data-testid="sessions-table-container"
    >
      <div className="overflow-x-auto">
        <table className="data-table" data-testid="sessions-table">
          <thead>
            <tr>
              <th className="w-10"></th>
              <th>Created</th>
              <th>Score</th>
              <th>Status</th>
              <th>Product</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Channel</th>
              <th>Demo</th>
              <th>Handoff</th>
              <th>Hot Alert</th>
              <th>Handoff At</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <SessionRow
                key={session.id}
                session={session}
                isExpanded={expandedRows.has(session.id)}
                onToggle={() => toggleRow(session.id)}
                onSessionClick={onSessionClick}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionsTable;
